"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.RainforestPaymentSearchResponse = exports.RainforestPaymentSearchResult = exports.CurrencyCode = exports.RainforestPaymentSearchQuery = exports.RainforestWebhookEventTransport = exports.RainforestWebhookPayinEventTypes = exports.RainforestWebhookAchReturnEventTypes = exports.RainforestWebhookPaymentMethodEventTypes = exports.RainforestWebhookMerchantEventTypes = exports.RainforestWebhookMerchantApplicationEventTypes = exports.RainforestWebhookChargebackEventTypes = exports.RainforestRefundOrVoidCreateTransport = exports.RainforestRefundOrVoidTransport = exports.RainforestPayinTransport = exports.RainforestPayinCreateTransport = exports.RainforestPaymentMethodConfigCreateTransport = exports.RainforestSessionCreateTransport = exports.AutopaySourceProcessorTransport = exports.AUTOPAY_PROCESSOR_CUSTOMER_PAYMENT_METHOD = exports.SupportedPaymentProcessorTransport = exports.PAYMENT_PROCESSOR_RAINFOREST = exports.PAYMENT_PROCESSOR_STRIPE = void 0;
const typebox_1 = require("@sinclair/typebox");
const customer_1 = require("../customer");
const currency_1 = require("../stripe-handler/currency");
exports.PAYMENT_PROCESSOR_STRIPE = 'STRIPE';
exports.PAYMENT_PROCESSOR_RAINFOREST = 'RAINFOREST';
exports.SupportedPaymentProcessorTransport = typebox_1.Type.Union([
    typebox_1.Type.Literal(exports.PAYMENT_PROCESSOR_STRIPE),
    typebox_1.Type.Literal(exports.PAYMENT_PROCESSOR_RAINFOREST),
]);
// Not a payment processor, but another layer of indirection. Represents a `customer_payment_methods` row ID
// rather than a processor token.
exports.AUTOPAY_PROCESSOR_CUSTOMER_PAYMENT_METHOD = 'CUSTOMER_PAYMENT_METHOD';
// What a stored autopay configuration may name. Includes "CUSTOMER_PAYMENT_METHOD", though not itself
// a specific payment processor, as a layer of abstraction over supported payment processors for UX purposes.
exports.AutopaySourceProcessorTransport = typebox_1.Type.Union([
    ...exports.SupportedPaymentProcessorTransport.anyOf,
    typebox_1.Type.Literal(exports.AUTOPAY_PROCESSOR_CUSTOMER_PAYMENT_METHOD),
]);
exports.RainforestSessionCreateTransport = typebox_1.Type.Object({
    // One or more permissions/groups defined by Rainforest: https://docs.rainforestpay.com/docs/component-sessions
    permissions: typebox_1.Type.Array(typebox_1.Type.String(), { minItems: 1 }),
    // Set to `true` to allow starting a session for a hauler that has started but not yet completed Rainforest merchant onboarding
    allowPendingOnboarding: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
});
exports.RainforestPaymentMethodConfigCreateTransport = typebox_1.Type.Object({
    sessionKey: typebox_1.Type.String(),
    idempotencyKey: typebox_1.Type.String({ format: 'uuid' }),
    // Payment method configs may be requested for customers not yet created in our system
    customer: typebox_1.Type.Intersect([
        typebox_1.Type.Partial(typebox_1.Type.Omit(customer_1.BaseCustomer, ['contacts'])),
        typebox_1.Type.Required(typebox_1.Type.Pick(customer_1.BaseCustomer, ['billingAddress'])),
        typebox_1.Type.Object({
            contacts: typebox_1.Type.Array(typebox_1.Type.Partial(customer_1.BaseCustomerContact)),
        }),
    ]),
    cardholderName: typebox_1.Type.String(),
});
exports.RainforestPayinCreateTransport = typebox_1.Type.Object({
    paymentMethodID: typebox_1.Type.String({ minLength: 1 }),
    amount: typebox_1.Type.Integer({ exclusiveMinimum: 0 }),
    // The platform fee is resolved from whichever of these is provided:
    // - `receivableIDs`: server derives the service line(s) from the receivable(s) being paid and
    //   applies the highest fee across them. Use this for invoice-payment flows — it prevents the
    //   client from arbitraging a cheaper service line.
    // - `serviceLineKey`: used by flows that charge before a receivable exists (order create, pickup,
    //   autopay) and therefore know their service line directly.
    // Provide exactly one. `receivableIDs` wins if both are present.
    serviceLineKey: typebox_1.Type.Optional(typebox_1.Type.String()),
    receivableIDs: typebox_1.Type.Optional(typebox_1.Type.Array(typebox_1.Type.String({ format: 'uuid' }), { minItems: 1 })),
    idempotencyKey: typebox_1.Type.Optional(typebox_1.Type.String()),
    customerID: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'uuid' })),
});
// Other payment processing-related routes return the Rainforest API response directly, but payin creation
// returns a custom response so that we can, for example, abstract the differences between credit card and
// Apple Pay payins so that the client doesn't have to deal with them
exports.RainforestPayinTransport = typebox_1.Type.Object({
    payinID: typebox_1.Type.String(),
    paymentMethod: typebox_1.Type.String(),
    last4: typebox_1.Type.Optional(typebox_1.Type.String()),
    amount: typebox_1.Type.Integer(),
});
exports.RainforestRefundOrVoidTransport = typebox_1.Type.Object({
    refundID: typebox_1.Type.Optional(typebox_1.Type.String()),
    isVoid: typebox_1.Type.Boolean(), // true if the payin was voided, false if refunded
    amount: typebox_1.Type.Integer({ exclusiveMinimum: 0 }),
});
exports.RainforestRefundOrVoidCreateTransport = typebox_1.Type.Object({
    payinID: typebox_1.Type.String(),
    amount: typebox_1.Type.Optional(typebox_1.Type.Integer({ exclusiveMinimum: 0 })),
    reason: typebox_1.Type.Union([typebox_1.Type.Literal('REQUESTED_BY_CUSTOMER'), typebox_1.Type.Literal('DUPLICATE'), typebox_1.Type.Literal('FRAUDULENT')]),
    // Client-generated per refund intent. Distinct UUIDs allow legitimate same-amount partial refunds on
    // the same payin while still de-duping retries of the same intent.
    idempotencyKey: typebox_1.Type.String({ format: 'uuid' }),
});
exports.RainforestWebhookChargebackEventTypes = [
    'chargeback.inquiry_action_required',
    'chargeback.inquiry_processing',
    'chargeback.dispute_action_required',
    'chargeback.chargeback_processing',
    'chargeback.provisional_win',
    'chargeback.lost',
    'chargeback.won',
];
// Events related to the hauler's initial application to set up Wayste Billing through Rainforest
exports.RainforestWebhookMerchantApplicationEventTypes = [
    'merchant_application.created',
    'merchant_application.in_progress',
    'merchant_application.processing',
    'merchant_application.needs_information',
    'merchant_application.in_review',
    'merchant_application.completed',
    'merchant_application.declined',
];
// Events related to the Rainforest merchant's general lifecycle
exports.RainforestWebhookMerchantEventTypes = ['merchant.suspended', 'merchant.deactivated'];
// Events related to Rainforest payment methods, especially pertinent to Card Account Updater (CAU)
exports.RainforestWebhookPaymentMethodEventTypes = ['payment_method.replaced'];
// Events related to Rainforest ACH returns (akin to chargebacks for cards)
exports.RainforestWebhookAchReturnEventTypes = ['ach_return.created'];
// Events related to Rainforest payins (incoming payments from a merchant's customers)
exports.RainforestWebhookPayinEventTypes = ['payin.succeeded', 'payin.failed'];
// Webhook event type reference: https://docs.rainforestpay.com/docs/webhooks-and-notifications
exports.RainforestWebhookEventTransport = typebox_1.Type.Object({
    event_type: typebox_1.Type.Union([
        ...exports.RainforestWebhookChargebackEventTypes,
        ...exports.RainforestWebhookMerchantApplicationEventTypes,
        ...exports.RainforestWebhookMerchantEventTypes,
        ...exports.RainforestWebhookPaymentMethodEventTypes,
        ...exports.RainforestWebhookPayinEventTypes,
        ...exports.RainforestWebhookAchReturnEventTypes,
    ].map((eventType) => typebox_1.Type.Literal(eventType))),
    data: typebox_1.Type.Unknown(),
});
/**
 * Admin-portal-only search of Rainforest payments. Used by the "Manage Rainforest" page.
 * Date range + billingContactName are filtered server-side via Rainforest's listPayments API.
 * lastFour is post-filtered server-side from each payment's payment_method.card.last_four,
 * because Rainforest doesn't expose last-four as a native query filter.
 */
exports.RainforestPaymentSearchQuery = typebox_1.Type.Object({
    createdAtStart: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    createdAtEnd: typebox_1.Type.Optional(typebox_1.Type.String({ format: 'date-time' })),
    billingContactName: typebox_1.Type.Optional(typebox_1.Type.String({ maxLength: 200 })),
    lastFour: typebox_1.Type.Optional(typebox_1.Type.String({ pattern: '^[0-9]{4}$' })),
    limit: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 1, maximum: 100 })),
    offset: typebox_1.Type.Optional(typebox_1.Type.Integer({ minimum: 0 })),
});
// Rainforest only supports USD right now, but this enum is more for formatting different currency values more broadly
// Eventually, move the source Currencies from StripeHandler here and replace references with these Invoice-based types
exports.CurrencyCode = typebox_1.Type.Union(currency_1.Currencies.map((curr) => typebox_1.Type.Literal(curr.toUpperCase())));
exports.RainforestPaymentSearchResult = typebox_1.Type.Object({
    id: typebox_1.Type.String(),
    paymentType: typebox_1.Type.Optional(typebox_1.Type.String()),
    createdAt: typebox_1.Type.Optional(typebox_1.Type.String()),
    amount: typebox_1.Type.Optional(typebox_1.Type.Integer()),
    currencyCode: typebox_1.Type.Optional(exports.CurrencyCode),
    status: typebox_1.Type.Optional(typebox_1.Type.String()),
    methodType: typebox_1.Type.Optional(typebox_1.Type.String()),
    lastFour: typebox_1.Type.Optional(typebox_1.Type.String()),
    cardBrand: typebox_1.Type.Optional(typebox_1.Type.String()),
    billingContactName: typebox_1.Type.Optional(typebox_1.Type.String()),
    paymentMethodID: typebox_1.Type.Optional(typebox_1.Type.String()),
});
exports.RainforestPaymentSearchResponse = typebox_1.Type.Object({
    results: typebox_1.Type.Array(exports.RainforestPaymentSearchResult),
    totalResults: typebox_1.Type.Optional(typebox_1.Type.Integer()),
    /**
     * True when `lastFour` was supplied AND the server-side date/name filters matched more records
     * than we scanned. The UI should warn the user to narrow filters because we may have missed
     * matching cards beyond the scan cap.
     */
    scanIncomplete: typebox_1.Type.Optional(typebox_1.Type.Boolean()),
});
//# sourceMappingURL=payment-processing.js.map